import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'shared/components/UC/Button';
import Badge from 'shared/components/UC/Badge';
import adminService from '../services/adminService';
import styles from './AdminHome.module.css';

interface Contest {
  id: string;
  name: string;
  description: string;
  year: number | null;
  isActive: boolean;
  isPublished: boolean;
  openDate: string | null;
  deadline: string;
  applicationCount: number;
}

const contestStatus = (contest: Contest): 'open' | 'closed' => {
  if (!contest.isActive) return 'closed';
  const deadlineMs = new Date(contest.deadline).getTime();
  if (deadlineMs < Date.now()) return 'closed';
  return 'open';
};

const STATUS_LABEL: Record<string, string> = {
  open: 'Abierto',
  closed: 'Cerrado',
};

const AdminHome: React.FC = () => {
  const navigate = useNavigate();
  const [contests, setContests] = useState<Contest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminService
      .getContests()
      .then(setContests)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className={styles.page}>
        <p>Cargando concursos...</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Panel de Administración</h1>
        <p className={styles.subtitle}>
          Seleccione un concurso para gestionarlo.
        </p>
      </div>

      {contests.length === 0 ? (
        <div className={styles.emptyState}>No hay concursos configurados.</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Concurso</th>
                <th className={styles.th}>Año</th>
                <th className={styles.th}>Estado</th>
                <th className={styles.th}>Publicación</th>
                <th className={styles.th}>Apertura</th>
                <th className={styles.th}>Cierre</th>
                <th className={`${styles.th} ${styles.thRight}`}>
                  Postulaciones
                </th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {contests.map((contest) => {
                const status = contestStatus(contest);
                const fmt = (iso: string | null) => {
                  if (!iso) return '—';
                  const d = new Date(iso);
                  return isNaN(d.getTime())
                    ? '—'
                    : d.toLocaleDateString('es-CL', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      });
                };
                const openDateLabel = fmt(contest.openDate);
                const deadlineLabel = fmt(contest.deadline);

                return (
                  <tr key={contest.id} className={styles.row}>
                    <td className={styles.td}>
                      <span className={styles.contestName}>{contest.name}</span>
                    </td>
                    <td className={styles.td}>{contest.year ?? '—'}</td>
                    <td className={styles.td}>
                      <Badge
                        label={STATUS_LABEL[status]}
                        variant={status === 'open' ? 'success' : 'secondary'}
                      />
                    </td>
                    <td className={styles.td}>
                      {status === 'closed' ? (
                        <span className={styles.publishedNull}>—</span>
                      ) : contest.isPublished ? (
                        <Badge label="Publicado" variant="success" />
                      ) : (
                        <Badge label="No publicado" variant="secondary" />
                      )}
                    </td>
                    <td className={styles.td}>{openDateLabel}</td>
                    <td className={styles.td}>{deadlineLabel}</td>
                    <td className={`${styles.td} ${styles.tdRight}`}>
                      {contest.applicationCount}
                    </td>
                    <td className={`${styles.td} ${styles.tdAction}`}>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          navigate(`/admin/dashboard/${contest.id}`)
                        }
                      >
                        Gestionar
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminHome;
